import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const UsersPage = () => {
  const { isDarkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'USER',
    password: ''
  });
  const { user } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getUsers();
      setUsers(data.users || []);
      setError(null);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'USER',
      password: ''
    });
  };

  const handleCreateUser = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEditUser = (userData) => {
    setEditingUser(userData);
    setFormData({
      firstName: userData.first_name,
      lastName: userData.last_name,
      email: userData.email,
      role: userData.role,
      password: '' // Don't pre-fill password
    });
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userData) => {
    if (!window.confirm(`Are you sure you want to delete user ${userData.first_name} ${userData.last_name}?`)) {
      return;
    }

    try {
      await apiService.deleteUser(userData.id);
      setUsers(users.filter(u => u.id !== userData.id));
      setError(null);
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    }
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    try {
      const newUser = await apiService.createUser(formData);
      setUsers([...users, newUser.user]);
      setShowCreateModal(false);
      resetForm();
      setError(null);
    } catch (err) {
      setError('Failed to create user: ' + err.message);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role
      };
      
      // Update user basic info
      const updatedUser = await apiService.updateUser(editingUser.id, updateData);
      setUsers(users.map(u => u.id === editingUser.id ? updatedUser.user : u));
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      setError(null);
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading users...</p>
      </div>
    );
  }

  // Check if user has permission to view users page
  if (!user || !['ADMIN', 'SUPPORT'].includes(user.role)) {
    return (
      <div style={{ 
        padding: '2rem',
        backgroundColor: '#f8d7da',
        color: '#721c24',
        borderRadius: '0.5rem',
        margin: '1rem'
      }}>
        Access denied. You need ADMIN or SUPPORT role to view users.
      </div>
    );
  }

  return (
    <div style={{ 
      padding: 'clamp(1rem, 3vw, 1.5rem)', 
      maxWidth: '1200px', 
      margin: '0 auto' 
    }}>
      <div style={{
        backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
        border: isDarkMode ? '1px solid #404040' : 'none'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h1 style={{ margin: 0, color: isDarkMode ? '#ffffff' : '#333' }}>User Management</h1>
          {user.role === 'ADMIN' && (
            <button
              onClick={handleCreateUser}
              style={{
                backgroundColor: isDarkMode ? '#4dabf7' : '#007bff',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              + Create User
            </button>
          )}
        </div>

        {error && (
          <div style={{
            backgroundColor: isDarkMode ? '#3a1e1e' : '#f8d7da',
            color: isDarkMode ? '#ff6b6b' : '#721c24',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: isDarkMode ? '1px solid #ff6b6b' : 'none'
          }}>
            {error}
          </div>
        )}

        <div style={{
          backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
          borderRadius: '0.5rem',
          boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          border: isDarkMode ? '1px solid #404040' : 'none'
        }}>
          {/* Table Container */}
          <div style={{ minWidth: '800px' }}>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr 1fr 1fr 150px',
              backgroundColor: isDarkMode ? '#333' : '#f8f9fa',
              padding: '1rem',
              fontWeight: '600',
              color: isDarkMode ? '#ffffff' : '#333',
              borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6'
            }}>
            <div>Name</div>
            <div>Email</div>
            <div>Role</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {/* Table Rows */}
          {users.map(userData => (
            <div key={userData.id} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr 1fr 1fr 150px',
              padding: '1rem',
              borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
              alignItems: 'center',
              color: isDarkMode ? '#ffffff' : '#333'
            }}>
              <div>{userData.first_name} {userData.last_name}</div>
              <div>{userData.email}</div>
              <div>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '1rem',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  background: userData.role === 'ADMIN' ? '#dc3545' : 
                           userData.role === 'SUPPORT' ? '#fd7e14' :
                           userData.role === 'VIP_USER' ? '#6f42c1' : 
                           userData.role === 'SUPERUSER' ? '#000000' : '#28a745',
                  color: 'white'
                }}>
                  {userData.role}
                </span>
              </div>
              <div>{userData.is_active ? 'Active' : 'Inactive'}</div>
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                flexWrap: 'wrap',
                justifyContent: 'flex-start'
              }}>
                <button 
                  onClick={() => handleEditUser(userData)}
                  style={{
                    background: isDarkMode ? '#555' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Edit
                </button>
                {userData.role !== 'ADMIN' && user.role === 'ADMIN' && (
                  <button 
                    onClick={() => handleDeleteUser(userData)}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}

            {users.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: isDarkMode ? '#b3b3b3' : '#666'
              }}>
                No users found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
            padding: 'clamp(1rem, 4vw, 2rem)',
            borderRadius: '0.5rem',
            width: 'min(500px, 90vw)',
            maxHeight: '80vh',
            overflowY: 'auto',
            margin: '1rem',
            border: isDarkMode ? '1px solid #404040' : 'none'
          }}>
            <h2 style={{ 
              marginTop: 0, 
              color: isDarkMode ? '#ffffff' : '#333' 
            }}>Create New User</h2>
            <form onSubmit={handleSubmitCreate}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  color: isDarkMode ? '#ffffff' : '#333'
                }}>
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  color: isDarkMode ? '#ffffff' : '#333'
                }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  color: isDarkMode ? '#ffffff' : '#333'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  color: isDarkMode ? '#ffffff' : '#333'
                }}>
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333'
                  }}
                >
                  <option value="USER">User</option>
                  <option value="VIP_USER">VIP User</option>
                  <option value="SUPPORT">Support</option>
                  {user.role === 'ADMIN' && <option value="ADMIN">Admin</option>}
                  {user.role === 'SUPERUSER' && <option value="SUPERUSER">SuperUser</option>}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  color: isDarkMode ? '#ffffff' : '#333'
                }}>
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333'
                  }}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
                    borderRadius: '0.25rem',
                    backgroundColor: isDarkMode ? '#555' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333',
                    cursor: 'pointer',
                    minWidth: '100px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '0.25rem',
                    backgroundColor: isDarkMode ? '#4dabf7' : '#007bff',
                    color: 'white',
                    cursor: 'pointer',
                    minWidth: '100px'
                  }}
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
            padding: 'clamp(1rem, 4vw, 2rem)',
            borderRadius: '0.5rem',
            width: 'min(500px, 90vw)',
            maxHeight: '80vh',
            overflowY: 'auto',
            margin: '1rem',
            border: isDarkMode ? '1px solid #404040' : 'none'
          }}>
            <h2 style={{ 
              marginTop: 0,
              color: isDarkMode ? '#ffffff' : '#333'
            }}>Edit User</h2>
            <form onSubmit={handleSubmitEdit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  disabled={editingUser?.role === 'ADMIN' && user.role !== 'ADMIN'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    backgroundColor: editingUser?.role === 'ADMIN' && user.role !== 'ADMIN' ? '#f5f5f5' : 'white'
                  }}
                >
                  <option value="USER">User</option>
                  <option value="VIP_USER">VIP User</option>
                  <option value="SUPPORT">Support</option>
                  {user.role === 'ADMIN' && <option value="ADMIN">Admin</option>}
                  {user.role === 'SUPERUSER' && <option value="SUPERUSER">SuperUser</option>}
                </select>
              </div>

              <div style={{ 
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '0.25rem',
                border: '1px solid #dee2e6'
              }}>
                <p style={{ 
                  margin: '0 0 0.5rem 0', 
                  color: '#6c757d',
                  fontSize: '0.875rem'
                }}>
                  Note: Password changes require the current password and must be done separately.
                </p>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '100px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '0.25rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    cursor: 'pointer',
                    minWidth: '100px'
                  }}
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage; 